import { useState, useEffect, useRef } from 'react';

import { appBorders } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { axiosInstance as axios } from '@backrs_ui/grow-ui/src/core/axios';
import {
  SolutionSessionMessage,
  SENDER_TYPE,
  MESSAGE_MODE,
} from '@backrs_ui/grow-ui/src/models/models';
import {
  setCollectedData,
  setIsLoadingResources,
  setMainResponseData,
} from '@backrs_ui/grow-ui/src/redux/boxData/boxData';
import {
  addSolutionSessionMessage,
  setSolutionSessionMessages,
  setSessionId,
} from '@backrs_ui/grow-ui/src/redux/coachLubav/coachLubav';
import { useAppDispatch, useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import {
  getSessionState,
  getActiveLubavSession,
  createSessionAction,
  typeGuardForSolutionSessionId,
} from '@backrs_ui/grow-ui/src/redux/lubavSessionState/lubavSessionState.utils';
import {
  setChatMode,
  setUserMessage,
} from '@backrs_ui/grow-ui/src/redux/solutionSession/solutionSession';
import { selectUserId } from '@backrs_ui/grow-ui/src/redux/user/user';

import { Box, SxProps, Theme } from '@mui/material';
import ReactMarkdown from 'react-markdown';

import { InputBox } from './InputBox';
import { RenderQuestion } from './Question';
import { finalMessages } from './SessionStrings';
import { ChatProfilePicture } from '../Lubav/ChatProfilePicture';
import { EditMessageBox, UserMessageActionsButton } from '../Lubav/EditMessageBox';
import { EnterButton } from '../Lubav/EnterButton';
import { SuggestedPrompts } from '../Lubav/SuggestedPrompts';
import { TypingIndicator } from '../Lubav/TypingIndicator';

export const ChatBox = () => {
  const activeSession = useAppSelector(getActiveLubavSession);

  const { openAIModel: LLM, solutionSessionMessages } = useAppSelector(
    (state) => getSessionState(state, activeSession).coachLubav,
  );
  const { sessionId } = useAppSelector((state) => getSessionState(state, activeSession).coachLubav);
  const { loading: isLoadingResources } = useAppSelector(
    (state) => getSessionState(state, activeSession).boxData,
  );
  const { chatMode } = useAppSelector(
    (state) => getSessionState(state, activeSession).solutionSession,
  );

  const userId = useAppSelector(selectUserId);

  const dispatch = useAppDispatch();

  const scrollRef = useRef<HTMLDivElement>(null);

  const [isTyping, setIsTyping] = useState(false);
  const [freeformIsActive, setFreeformIsActive] = useState(false);
  const [freeformIsNumeric, setFreeformIsNumeric] = useState(false);

  const isEditingRef = useRef(false);

  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [editingText, setEditingText] = useState('');

  useEffect(() => {
    const initializeChat = async () => {
      if (solutionSessionMessages.length === 0 && sessionId) {
        dispatch(createSessionAction(setChatMode, activeSession)(false));
        sendQAMessage('<START>', undefined);
      }
    };

    initializeChat();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [solutionSessionMessages.length, sessionId]);

  useEffect(() => {
    const scrollContainer = scrollRef.current;

    if (scrollContainer) {
      scrollContainer.scrollTop = scrollContainer.scrollHeight;
    }
  }, [solutionSessionMessages, isTyping]);

  if (!activeSession || !typeGuardForSolutionSessionId(activeSession)) {
    return null;
  }

  const handleEditSubmitChat = async (queryId: string, newText: string) => {
    try {
      isEditingRef.current = true;
      setEditingMessageId(null);
      const messageIndex = solutionSessionMessages.findIndex((msg) => msg.id === queryId);

      const updatedMessages: SolutionSessionMessage[] = [
        ...solutionSessionMessages.slice(0, messageIndex),
      ];

      dispatch(createSessionAction(setSolutionSessionMessages, activeSession)(updatedMessages));

      if (chatMode) {
        const response = await axios.post('/api/office-hours/get-new-session-id-for-message-edit', {
          session_id: sessionId,
          query_id: queryId,
        });
        const newSessionId = response.data.session_id;

        dispatch(createSessionAction(setSessionId, activeSession)(newSessionId));

        await sendChatMessage(newText, undefined, updatedMessages, newSessionId);
      } else {
        await sendQAMessage(newText, newText);
      }

      isEditingRef.current = false;
    } catch (error) {
      console.error('Error editing message:', error);
      isEditingRef.current = false;
    }
  };

  async function getResponse(noResources = false) {
    if (!activeSession) {
      return null;
    }

    dispatch(createSessionAction(setIsLoadingResources, activeSession)(true));
    try {
      const mainResponse = await axios.post('/api/solution-session/get-response', {
        session_id: sessionId,
        user_id: `${userId}`,
        llm: LLM,
        solution_session: activeSession,
        no_resources: noResources,
      });

      dispatch(createSessionAction(setIsLoadingResources, activeSession)(false));

      dispatch(createSessionAction(setMainResponseData, activeSession)(mainResponse.data.output));
      dispatch(
        createSessionAction(
          setSolutionSessionMessages,
          activeSession,
        )([
          ...solutionSessionMessages,
          {
            text: mainResponse.data.output.final_message.text,
            type: 'final_message',
            sender: SENDER_TYPE.AI,
          },
        ]),
      );
    } catch (error) {
      dispatch(createSessionAction(setIsLoadingResources, activeSession)(false));

      // for now we assume an overload error if we get any error
      // TODO: handle errors on a case by case basis
      dispatch(
        createSessionAction(
          addSolutionSessionMessage,
          activeSession,
        )({
          text: appStrings.modules.chatBox.overloadErr,
          sender: SENDER_TYPE.AI,
        }),
      );
      console.error('Error fetching response:', error);
    }
  }

  async function sendQAMessage(input: string, message?: string | string[]) {
    if (!activeSession) {
      return null;
    }

    let newSessionId = null;

    // Determine the current messages based on whether we are editing.
    const currentMessages = (() => {
      if (editingMessageId && !chatMode) {
        // Find the index of the message being edited.
        const messageIndex = solutionSessionMessages.findIndex(
          (msg) => msg.id === editingMessageId,
        );

        // Remove the edited message and everything after it.
        return solutionSessionMessages.slice(0, messageIndex);
      }

      return solutionSessionMessages;
    })();

    // If we are editing, perform the async actions related to that.
    if (editingMessageId && !chatMode) {
      isEditingRef.current = true;
      // Update state with the pruned list.
      dispatch(createSessionAction(setSolutionSessionMessages, activeSession)(currentMessages));
      // Request a new session ID for this edited message.
      const response = await axios.post(
        '/api/solution-session/get-new-session-id-for-message-edit',
        {
          session_id: sessionId,
          query_id: editingMessageId,
        },
      );

      newSessionId = response.data.session_id;

      dispatch(createSessionAction(setSessionId, activeSession)(newSessionId));
      setEditingMessageId(null);
    }

    // If message is an array, join it into a string.
    if (Array.isArray(message)) {
      message = message.join(', ');
    }

    // Create a new messages array that includes the user message optimistically.
    const optimisticMessages = message
      ? [
          ...currentMessages,
          {
            text: message,
            sender: SENDER_TYPE.USER,
            mode: MESSAGE_MODE.QA,
          },
        ]
      : currentMessages;

    // Update the UI with the optimistic message.
    dispatch(createSessionAction(setSolutionSessionMessages, activeSession)(optimisticMessages));

    setIsTyping(true);
    setFreeformIsActive(false);
    dispatch(setUserMessage(''));

    try {
      const mainResponse = await axios.post('/api/solution-session/send-qa-message', {
        text: input,
        session_id: newSessionId || sessionId,
        user_id: userId,
        llm: LLM,
        solution_session: activeSession,
      });

      const question = mainResponse.data.question;
      const { query_id, response_id } = mainResponse.data;

      if (mainResponse.data.finished) {
        dispatch(createSessionAction(setChatMode, activeSession)(true));
        getResponse();
      }

      setIsTyping(false);

      // Remove the temporary user message (assumed to be the last one) and add the finalized one plus the AI message.
      dispatch(
        createSessionAction(
          setSolutionSessionMessages,
          activeSession,
        )([
          ...optimisticMessages.slice(0, -1),
          ...(message
            ? [
                {
                  text: message as string,
                  sender: SENDER_TYPE.USER,
                  id: query_id,
                  mode: MESSAGE_MODE.QA,
                },
              ]
            : []),
          {
            text: mainResponse.data.text,
            sender: SENDER_TYPE.AI,
            id: response_id,
            mode: MESSAGE_MODE.QA,
            ...(question && {
              question: question.text,
              type: question.type,
              options: question.options,
              include_freeform: question.include_freeform,
              required: question.required,
              user_selection_options: question.user_selection_options,
              placeholder: question.placeholder,
            }),
          },
        ]),
      );

      const collectedData = await axios.post(
        '/api/solution-session/get-collected-data',
        newSessionId || sessionId,
      );

      dispatch(createSessionAction(setCollectedData, activeSession)(collectedData.data));
    } catch (error) {
      setIsTyping(false);

      // Use currentMessages (or optimisticMessages) to ensure we're not using stale data.
      dispatch(
        createSessionAction(
          addSolutionSessionMessage,
          activeSession,
        )({
          text: appStrings.modules.chatBox.overloadErr,
          sender: SENDER_TYPE.AI,
        }),
      );
      console.error('Error fetching response:', error);
    }

    isEditingRef.current = false;
  }

  async function sendChatMessage(
    input: string,
    _?: string,
    currentMessageList?: SolutionSessionMessage[],
    newSessionId?: string,
  ) {
    if (!activeSession) {
      return null;
    }

    const messagesToUse = currentMessageList || solutionSessionMessages;

    const sessionIdToUse = newSessionId || sessionId;

    const updatedMessages: SolutionSessionMessage[] = input
      ? [...messagesToUse, { text: input, sender: SENDER_TYPE.USER }]
      : messagesToUse;

    dispatch(createSessionAction(setSolutionSessionMessages, activeSession)(updatedMessages));

    setIsTyping(true);
    dispatch(setUserMessage(''));
    try {
      const mainResponse = await axios.post('/api/solution-session/send-chat-message', {
        text: input,
        session_id: sessionIdToUse,
        user_id: userId,
        llm: LLM,
        solution_session: activeSession,
      });

      const { query_id, response_id } = mainResponse.data;

      setIsTyping(false);
      dispatch(
        createSessionAction(
          setSolutionSessionMessages,
          activeSession,
        )([
          ...updatedMessages.slice(0, -1),
          {
            text: input,
            sender: SENDER_TYPE.USER,
            id: query_id,
            mode: MESSAGE_MODE.CHAT,
          },
          {
            text: mainResponse.data.text,
            sender: SENDER_TYPE.AI,
            id: response_id,
            ask_to_regenerate: mainResponse.data.ask_to_regenerate,
            mode: MESSAGE_MODE.CHAT,
          },
        ]),
      );
    } catch (error) {
      setIsTyping(false);

      // for now we assume an overload error if we get any error
      // TODO: handle errors on a case by case basis
      dispatch(
        createSessionAction(
          setSolutionSessionMessages,
          activeSession,
        )([
          ...solutionSessionMessages,
          { text: appStrings.modules.chatBox.overloadErr, sender: SENDER_TYPE.AI },
        ]),
      );
      console.error('Error fetching response:', error);
    }
  }

  const handleEdit = async (queryId: string) => {
    try {
      // if we're already editing this message, cancel the edit
      if (editingMessageId === queryId) {
        setEditingMessageId(null);
        setEditingText('');
        setFreeformIsActive(false);

        return;
      }

      const messageIndex = solutionSessionMessages.findIndex((msg) => msg.id === queryId);
      const messageText = solutionSessionMessages[messageIndex].text;

      setEditingMessageId(queryId);
      setEditingText(messageText || '');
      setFreeformIsActive(false);
    } catch (error) {
      console.error('Error editing message:', error);
    }
  };

  // helper function to determine if the message is being edited
  const isEditingMessage = (msg: SolutionSessionMessage, index: number) => {
    const type = solutionSessionMessages[index - 1]?.type;

    if (!type) {
      return false;
    }

    return (
      editingMessageId === msg.id &&
      (solutionSessionMessages[index - 1]?.include_freeform ||
        ['numeric', 'freeform'].includes(type) ||
        chatMode)
    );
  };

  // helper function to determine if the question should be shown
  const shouldShowQuestion = (msg: SolutionSessionMessage, index: number) => {
    return (
      (msg.sender === SENDER_TYPE.AI && index === solutionSessionMessages.length - 1) ||
      solutionSessionMessages[index + 1]?.id === editingMessageId
    );
  };

  // helper function to determine if the suggested prompts should be shown
  const shouldShowSuggestedPrompts = (msg: SolutionSessionMessage) =>
    chatMode &&
    msg.sender === SENDER_TYPE.AI &&
    msg.text === finalMessages[activeSession].followups[0];

  return (
    <Box sx={chatboxContainer}>
      <Box ref={scrollRef} sx={chatboxStyle}>
        {solutionSessionMessages?.map((msg, index) => (
          <Box key={index}>
            <Box sx={messageContainerStyle}>
              <ChatProfilePicture sender={msg.sender} />
              <Box sx={messageBoxStyle}>
                <Box sx={messageContentStyle}>
                  {isEditingMessage(msg, index) &&
                  (solutionSessionMessages[index - 1]?.include_freeform ||
                    solutionSessionMessages[index - 1]?.type === 'freeform' ||
                    solutionSessionMessages[index - 1]?.type === 'numeric' ||
                    msg.mode === MESSAGE_MODE.CHAT) ? (
                    <EditMessageBox
                      value={editingText}
                      onChange={setEditingText}
                      onSubmit={() => {
                        if (msg.id) {
                          handleEditSubmitChat(msg.id, editingText);
                        }
                      }}
                      isNumeric={solutionSessionMessages[index - 1]?.type === 'numeric'}
                    />
                  ) : (
                    <ReactMarkdown>
                      {msg.sender === SENDER_TYPE.AI && msg.question
                        ? `${msg.text} ${msg.question}`
                        : msg.text}
                    </ReactMarkdown>
                  )}

                  {msg.ask_to_regenerate &&
                    index === solutionSessionMessages.length - 1 &&
                    !isLoadingResources && (
                      <EnterButton onClick={() => getResponse()} label="Regenerate" />
                    )}

                  {shouldShowSuggestedPrompts(msg) && (
                    <SuggestedPrompts
                      prompts={finalMessages[activeSession].followups.map((prompt) => ({
                        display: prompt,
                      }))}
                      onSelect={sendChatMessage}
                    />
                  )}
                </Box>

                {msg.sender === SENDER_TYPE.USER && msg.id && (
                  <UserMessageActionsButton
                    onEdit={() => {
                      if (msg.id) {
                        handleEdit(msg.id);
                      }
                    }}
                  />
                )}
              </Box>
            </Box>

            <Box sx={questionContainerStyle}>
              <Box sx={questionContentStyle}>
                {shouldShowQuestion(msg, index) && userId && (
                  <RenderQuestion
                    message={msg}
                    sendQAMessage={sendQAMessage}
                    required={msg.required || false}
                    setFreeformIsActive={setFreeformIsActive}
                    setFreeformIsNumeric={setFreeformIsNumeric}
                    editingMessageId={editingMessageId}
                    userId={userId}
                  />
                )}
              </Box>
            </Box>
          </Box>
        ))}
        {isTyping && <TypingIndicator />}
      </Box>
      <Box sx={inputContainerStyle}>
        <InputBox
          sendQAMessage={sendQAMessage}
          sendChatMessage={sendChatMessage}
          isActive={freeformIsActive}
          isNumeric={freeformIsNumeric}
          placeholder={solutionSessionMessages[solutionSessionMessages.length - 1]?.placeholder}
        />
      </Box>
    </Box>
  );
};

const chatboxContainer: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  height: 'calc(100vh - 110px)',
  gap: '10px',
};

const chatboxStyle: SxProps<Theme> = {
  flexGrow: 1,
  overflowY: 'auto',
  backgroundColor: '#ffffff',
  borderRadius: '8px',
  padding: '20px',
  ...appBorders.backrsDropShadowBorders.black,
};

const messageContainerStyle: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'flex-start',
  fontSize: '11pt',
  color: '#000000',
};

const messageBoxStyle: SxProps<Theme> = {
  display: 'flex',
  wordBreak: 'break-word',
  width: '100%',
  gap: 1,
  alignItems: 'flex-start',
};

const messageContentStyle: SxProps<Theme> = {
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: 1,
  paddingRight: '40px',
};

const questionContainerStyle: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  marginLeft: '45px',
};

const questionContentStyle: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
};

const inputContainerStyle: SxProps<Theme> = {
  width: '100%',
  display: 'flex',
  flexDirection: 'column-reverse',
  alignItems: 'center',
  borderRadius: '8px',
};
